import {
    createStyles,
    Divider,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    WithStyles,
    withStyles,
} from '@material-ui/core';
import { inject } from 'mobx-react';
import moment from 'moment';
import React from 'react';
import { RouteComponentProps } from 'react-router';
import IDataTableManager from 'src/common/IDataTableManager';
import { IActionItem, IAssessment, IBranchKpi } from 'src/Definitions';
import { DashboardObservationStatsDto, IdentifierEntityDto } from 'src/generated_client';
import { IStores } from 'src/Stores';
import ActionStatuses from 'src/utils/ActionStatuses';
import AssessmentDialog from '../assessments/AssessmentDialog';
import ActionItemTableRow from '../dashboardRows/ActionItemRow/ActionItemTableRow';
import ObservationStats from '../dashboardRows/ObservationStats/ObservationStats';
import greenStatusPerson from '../images/green_status_person.png';
import noStatusPerson from '../images/no_status_person.png';
import redStatusPerson from '../images/red_status_person.png';
import yellowStatusPerson from '../images/yellow_status_person.png';

const styles = createStyles({
    assessmentDashboardItem: {
        margin: '10px',
        marginLeft: '25px',
    },
    noKPIMessage: {
        fontStyle: 'italic',
    },
    percentComplete: {
        color: '#777',
        fontSize: '8em',
        fontWeight: 'bold',
    },
    sectionHeader: {
        color: '#2429cb',
    },
    statusDiv: {
        display: 'flex',
    },
    statusDivMessage: {
        paddingLeft: '20px',
    },
    table: {
        minWidth: 650,
    },
});

interface IMatchParams {
    assessmentId: string;
}

interface IAssessmentDashboardState {
    assessmentId: string;
    kpiBranches: IdentifierEntityDto[];
    loaded: boolean;
}

interface IAssessmentDashboardProps {
    actionItems?: IActionItem[];
    assessment: IAssessment;
    branchKpis?: IBranchKpi[];
    dashboardStats?: DashboardObservationStatsDto;
    dueIn30DataTableManager?: IDataTableManager<IActionItem>;
    completedDataTableManager?: IDataTableManager<IActionItem>;
    isModalOpen?: boolean;
    getAssessmentById?: (assessmentId: string) => Promise<void>;
    getAssessmentDashboardObservationStats?: (
        branches: string,
        regions: string,
        assessmentId: string | undefined
    ) => Promise<void>;
    getBranchListKpis?: (targetBranchId: string, branchIds: string[]) => void;
    openDialogWithAssessment?: (assessment: IAssessment) => void;
    selectedAssessment?: IAssessment;
    setSelectedAssessments?: (assessments: IAssessment[]) => void;
    getAllActionItems?: (shouldLoadRegions: boolean, shouldLoadAssignees: boolean) => Promise<void>;
    getColumnSettingsAndActionItems?: () => Promise<void>;
}

class AssessmentDashboard extends React.Component<
    IAssessmentDashboardProps & WithStyles<typeof styles> & RouteComponentProps<IMatchParams>,
    IAssessmentDashboardState
> {
    public state: IAssessmentDashboardState = {
        assessmentId: this.props.match ? this.props.match.params.assessmentId : '',
        kpiBranches: [],
        loaded: false,
    };

    public async componentDidMount() {
        await this.pullInitialData();
        this.props.getAllActionItems!(false, false);
    }

    public async componentWillUpdate(nextProps: IAssessmentDashboardProps) {
        if (this.props.isModalOpen === true && nextProps.isModalOpen === false) {
            await this.pullInitialData();
        }
    }

    private async pullInitialData() {
        await Promise.all([
            this.props.getAssessmentById!(this.state.assessmentId),
            this.props.getAssessmentDashboardObservationStats!('', '', this.state.assessmentId),
            this.props.getColumnSettingsAndActionItems!(),
        ]).then(() => {
            const comparisonBranches = this.props.assessment.comparisonBranches;
            const targetBranch = this.props.assessment.targetBranch;
            this.props.getBranchListKpis!(
                targetBranch.id!,
                comparisonBranches.map((branch) => branch.id!)
            );
            const orderedBranches = [...comparisonBranches];
            orderedBranches.unshift(
                new IdentifierEntityDto({ name: targetBranch.name, id: targetBranch.id })
            );
            this.setState({ kpiBranches: orderedBranches, loaded: true });
            this.props.setSelectedAssessments!([this.props.assessment]);
        });
    }

    private getBranchKpi(
        branch: IdentifierEntityDto,
        kpi: IdentifierEntityDto
    ): IBranchKpi | undefined {
        if (this.props.branchKpis) {
            return this.props.branchKpis.find((branchKpi) => {
                return branchKpi.branch.id === branch.id && branchKpi.kpi.id === kpi.id;
            });
        }
        return undefined;
    }

    private getBranchKpiValue(branch: IdentifierEntityDto, kpi: IdentifierEntityDto) {
        const branchKpi = this.getBranchKpi(branch, kpi);
        if (branchKpi) {
            return branchKpi.value;
        }
        return undefined;
    }

    private renderTableHeaders() {
        return (
            <TableHead>
                <TableRow key="kpiBranches">
                    <TableCell />
                    {this.state.kpiBranches &&
                        this.state.kpiBranches.map((branch, idx) => (
                            <TableCell
                                key={branch.id}
                                align="center"
                                style={{
                                    backgroundColor: idx === 0 ? 'lightgrey' : 'white',
                                }}
                            >
                                <div>{idx === 0 ? 'Target Branch' : 'Comparison Branch'}</div>
                                <Typography
                                    key={`${branch.id} label`}
                                    variant="body2"
                                    component="h2"
                                >
                                    {branch.name}
                                </Typography>
                            </TableCell>
                        ))}
                </TableRow>
            </TableHead>
        );
    }

    private renderTableBody() {
        if (this.props.assessment.kpis && this.props.assessment.kpis.length === 0) {
            return (
                <p>
                    <span className={this.props.classes.noKPIMessage} id="noKPIMessage">
                        No KPI Measurements selected for this assessment.
                    </span>
                </p>
            );
        } else {
            return (
                <TableBody>
                    {this.props.assessment.kpis &&
                        this.props.assessment.kpis.map((kpi) => (
                            <TableRow key={kpi.name}>
                                <TableCell key={`${kpi.name} label`} component="th" scope="row">
                                    {kpi.name}
                                </TableCell>
                                {this.state.kpiBranches &&
                                    this.state.kpiBranches.map((branch, idx) => (
                                        <TableCell
                                            key={`${branch.name} ${kpi.name}`}
                                            align="right"
                                            style={{
                                                backgroundColor: idx === 0 ? 'lightgrey' : 'white',
                                            }}
                                        >
                                            {this.getBranchKpiValue(branch, kpi)}
                                        </TableCell>
                                    ))}
                            </TableRow>
                        ))}
                </TableBody>
            );
        }
    }

    private renderBranchCompareTable() {
        return (
            <>
                <h3 className={this.props.classes.sectionHeader}>Branch Comparison</h3>
                <Table className={this.props.classes.table} id="kpiComparisonTable">
                    {this.renderTableHeaders()}
                    {this.renderTableBody()}
                </Table>
            </>
        );
    }

    private assessmentPercentageComplete() {
        const numActionItems = this.props.actionItems ? this.props.actionItems!.length : 0;
        const numCompleted = this.props.actionItems
            ? this.props.actionItems!.filter(
                  (actionItem) => actionItem.status.name! === ActionStatuses.Completed.name
              ).length
            : 0;
        let percentCompleted = 0;
        if (numActionItems !== 0) {
            percentCompleted = numCompleted / numActionItems;
        }
        percentCompleted = Math.round(percentCompleted * 100);
        return (
            <Grid item sm={6}>
                <Grid container direction="column">
                    <Grid item sm={6}>
                        <p>The percentage of complete Action Items in this assessment is:</p>
                    </Grid>
                    <Grid item sm={6}>
                        <div className={this.props.classes.percentComplete}>
                            <span id="percentComplete">{percentCompleted}</span>%
                        </div>
                    </Grid>
                </Grid>
            </Grid>
        );
    }

    public openDialog = (assessment: IAssessment) => () => {
        this.props.openDialogWithAssessment!(assessment);
    };

    private assessmentStatus() {
        if (!this.props.assessment.dueDate || !this.props.assessment.visitEndedOn) {
            return (
                <Grid item sm={6}>
                    <Grid container direction="row">
                        <Grid item sm={12}>
                            <div className={this.props.classes.statusDiv}>
                                <img
                                    src={noStatusPerson}
                                    alt="no status"
                                    id="assessmentStatusImage"
                                />
                                <div className={this.props.classes.statusDivMessage}>
                                    <p>
                                        Unable to calculate end-date goal progress because either
                                        assessment due date or visit end date is not set.{' '}
                                        <a
                                            href="#"
                                            onClick={this.openDialog(this.props.assessment)}
                                        >
                                            Edit Assessment
                                        </a>
                                    </p>
                                </div>
                            </div>
                        </Grid>
                    </Grid>
                </Grid>
            );
        }
        const numActionItems = this.props.actionItems ? this.props.actionItems!.length : 0;
        const numRemaining = this.props.actionItems
            ? this.props.actionItems!.filter(
                  (actionItem) => actionItem.status.name! !== ActionStatuses.Completed.name
              ).length
            : 0;
        const earnedValue = numActionItems - numRemaining;
        const plannedDays = moment
            .duration(
                moment(this.props.assessment.dueDate).diff(
                    moment(this.props.assessment.visitEndedOn)
                )
            )
            .asDays();
        const plannedRate = numActionItems / plannedDays;
        const actualDays = moment
            .duration(moment().diff(moment(this.props.assessment.visitEndedOn!)))
            .asDays();
        let daysRemaining = Math.ceil(
            moment.duration(moment(this.props.assessment.dueDate).diff(moment())).asDays()
        );
        if (daysRemaining < 0) {
            daysRemaining = 0;
        }
        const plannedValue = plannedRate * actualDays;
        const earnedRatio = earnedValue / plannedValue;

        let statusMessage = '';
        let statusImage;
        let statusImageAlt = '';
        if (earnedRatio >= 1) {
            statusMessage = 'This assessment is on track to its end-date goal';
            statusImage = greenStatusPerson;
            statusImageAlt = 'green status';
        } else if (earnedRatio >= 0.85) {
            statusMessage = 'This assessment is at risk of its end-date goal';
            statusImage = yellowStatusPerson;
            statusImageAlt = 'yellow status';
        } else {
            statusMessage = 'This project is behind its end-date goal';
            statusImage = redStatusPerson;
            statusImageAlt = 'red status';
        }

        return (
            <Grid item sm={6}>
                <Grid container>
                    <Grid item sm={12}>
                        <div className={this.props.classes.statusDiv}>
                            <img
                                src={statusImage}
                                alt={statusImageAlt}
                                id="assessmentStatusImage"
                            />
                            <div className={this.props.classes.statusDivMessage}>
                                <p>
                                    {statusMessage}, with{' '}
                                    <span id="numDaysLeft">{daysRemaining}</span> days and{' '}
                                    <span id="numItemsLeft">{numRemaining + ' '}</span> action items
                                    remaining.
                                </p>
                            </div>
                        </div>
                    </Grid>
                </Grid>
            </Grid>
        );
    }

    private assessmentStatusRow() {
        if (this.props.assessment) {
            return (
                <Grid container direction="row" wrap="nowrap">
                    {this.assessmentPercentageComplete()}
                    {this.assessmentStatus()}
                </Grid>
            );
        } else {
            return (
                <Grid container direction="row" wrap="nowrap">
                    <p>No action items were found for this assessment.</p>
                </Grid>
            );
        }
    }

    public render() {
        const {
            assessment,
            dashboardStats,
            isModalOpen,
            selectedAssessment,
            dueIn30DataTableManager,
            completedDataTableManager,
        } = this.props;
        const dialogKey = selectedAssessment ? selectedAssessment.id : '-1';
        const assessmentDialog = isModalOpen && <AssessmentDialog key={dialogKey} />;
        return (
            <>
                <Grid container direction="column">
                    {assessment ? (
                        <>
                            <Grid item className={this.props.classes.assessmentDashboardItem}>
                                <h1>{assessment.name}</h1>
                            </Grid>
                            <Grid item>
                                <Divider />
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container direction="row" wrap="nowrap">
                                    <Grid
                                        item
                                        className={this.props.classes.assessmentDashboardItem}
                                        sm={6}
                                    >
                                        <Grid container direction="column">
                                            <Grid item>
                                                <h3 className={this.props.classes.sectionHeader}>
                                                    Improvement Strategy
                                                </h3>
                                            </Grid>
                                            <Grid item>
                                                <p id="improvementStrategy">
                                                    {assessment.improvementStrategy}
                                                </p>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid
                                        item
                                        className={this.props.classes.assessmentDashboardItem}
                                        sm={6}
                                    >
                                        <Grid container direction="column">
                                            <Grid item>
                                                <h3 className={this.props.classes.sectionHeader}>
                                                    Improvement Goals
                                                </h3>
                                            </Grid>
                                            <Grid item>
                                                <p id="improvementGoals">
                                                    {assessment.improvementGoals}
                                                </p>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item>
                                <Divider />
                            </Grid>
                            <Grid
                                item
                                xs={12}
                                className={this.props.classes.assessmentDashboardItem}
                            >
                                <h3 className={this.props.classes.sectionHeader}>
                                    Assessment Status
                                </h3>
                            </Grid>
                            <Grid
                                item
                                xs={12}
                                className={this.props.classes.assessmentDashboardItem}
                                id="assessmentStatusRow"
                            >
                                {this.assessmentStatusRow()}
                            </Grid>
                            <Grid item>
                                <Divider />
                            </Grid>
                            <Grid item>
                                {dashboardStats ? (
                                    <ObservationStats dashboardStats={dashboardStats} />
                                ) : (
                                    <></>
                                )}
                            </Grid>
                            <Grid item>
                                <Divider />
                            </Grid>
                            <Grid item>
                                {this.state.loaded ? (
                                    <div id="actionItemsRow">
                                        <ActionItemTableRow
                                            dueIn30DataTableManager={dueIn30DataTableManager}
                                            completedDataTableManager={completedDataTableManager}
                                        />
                                    </div>
                                ) : (
                                    <></>
                                )}
                            </Grid>
                            <Grid item>
                                <Divider />
                            </Grid>
                            <Grid
                                item
                                xs={12}
                                className={this.props.classes.assessmentDashboardItem}
                            >
                                {this.renderBranchCompareTable()}
                            </Grid>
                        </>
                    ) : (
                        <></>
                    )}
                </Grid>
                {assessmentDialog}
            </>
        );
    }
}

const InjectedAssessmentDashboard = inject<
    IStores,
    IAssessmentDashboardProps,
    Partial<IAssessmentDashboardProps>,
    any
>((stores: IStores) => ({
    actionItems: stores.actionItems.actionItems,
    assessment: stores.assessments.assessment,
    branchKpis: stores.branchKpis.branchKpis,
    branches: stores.branchKpis.branches,
    completedDataTableManager: stores.actionItems.completedDataTableStore,
    dashboardStats: stores.assessments.dashboardStats,
    dueIn30DataTableManager: stores.actionItems.dueIn30DataTableStore,
    getAllActionItems: stores.actionItems.getAllActionItems,
    getAssessmentById: stores.assessments.getAssessmentById,
    getAssessmentDashboardObservationStats: stores.assessments.getAssessmentDashboardStats,
    getBranchListKpis: stores.branchKpis.getBranchListKpis,
    getColumnSettingsAndActionItems: stores.actionItems.getColumnSettingsAndActionItems,
    isModalOpen: stores.assessments.isModalOpen,
    openDialogWithAssessment: stores.assessments.openDialogWithAssessment,
    selectedAssessment: stores.assessments.selectedAssessment,
    setSelectedAssessments: stores.actionItems.setSelectedAssessments,
}))(withStyles(styles)(AssessmentDashboard));

export default InjectedAssessmentDashboard;
export { IAssessmentDashboardProps, IAssessmentDashboardState };



// WEBPACK FOOTER //
// ./src/assessmentDashboard/AssessmentDashboard.tsx